import React, { useMemo, useState } from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Textarea from '@amzn/awsui-components-console/textarea';
import Multiselect from '@amzn/awsui-components-console/multiselect';
import Select from '@amzn/awsui-components-console/select';
import Alert from '@amzn/awsui-components-console/alert';
import Form from '@amzn/awsui-components-console/form';
import Spinner from '@amzn/awsui-components-console/spinner';

import type {
  MultiselectProps,
  SelectProps,
} from '@amzn/awsui-components-console';
import type { CreateStringGroupModalProps } from '../types';
import {
  SG_NAME_MAX_LENGTH,
  SG_DESCRIPTION_MAX_LENGTH,
  SG_NAME_MIN_LENGTH,
} from '../constants';
import {
  getNameError,
  getDescriptionError,
  getTeamNameError,
  getSourceLocaleError,
  getCxFamilyError,
  getCxIdsError,
} from './createStringGroupValidation';
import { useCreateStringGroupForm } from '../hooks/useCreateStringGroupForm';
import { useStringGroupNameCheck } from '../hooks/useStringGroupNameCheck';
import {
  useListCxFamiliesQuery,
  useDescribeCxFamilyQuery,
} from 'src/generated/graphql';
import RequiredAsterisk from 'src/components/common/util/FormHelpers';
import NoPiiNotice from 'src/common/ui/NoPiiNotice';
import { useTranslation } from 'src/i18n/shim/useTranslation';

/** Modal form for creating a new string group within a namespace. */
const CreateStringGroupModal: React.FC<CreateStringGroupModalProps> = ({
  visible,
  onDismiss,
  onSubmit,
  cxFamily,
  namespaceId,
  loadedStringGroupIds,
}) => {
  const { t } = useTranslation();
  const form = useCreateStringGroupForm();
  const checkNameTaken = useStringGroupNameCheck(
    namespaceId,
    loadedStringGroupIds,
  );

  // ─── CX Family state (only used when namespace has no default) ──────────
  const [selectedFamilyOption, setSelectedFamilyOption] =
    useState<SelectProps.Option | null>(null);

  // Determine the effective cxFamilyId: either from namespace default or user selection
  const effectiveCxFamilyId = cxFamily || selectedFamilyOption?.value || '';

  // ─── GraphQL queries ────────────────────────────────────────────────────
  // List all families (only needed when namespace has no default)
  const { data: listFamiliesData, loading: listFamiliesLoading } =
    useListCxFamiliesQuery({
      skip: !!cxFamily || !visible,
    });

  // Describe the selected/default family to get CxIds and sourceLocales
  const { data: describeFamilyData, loading: describeFamilyLoading } =
    useDescribeCxFamilyQuery({
      variables: { cxFamilyId: effectiveCxFamilyId },
      skip: !effectiveCxFamilyId || !visible,
    });

  // ─── Derived options from describeCxFamily response ─────────────────────
  const cxIdOptions: MultiselectProps.Option[] = useMemo(() => {
    const cxIds = describeFamilyData?.describeCxFamily?.cxFamily?.cxIds;
    if (!cxIds) return [];
    return cxIds.map((item) => ({
      label: item.cxId,
      value: item.cxId,
    }));
  }, [describeFamilyData]);

  const sourceLocaleOptions: SelectProps.Option[] = useMemo(() => {
    const familyDetail = describeFamilyData?.describeCxFamily?.cxFamily;
    if (!familyDetail) return [];

    // Step 1: Intersection of sourceLocales across all selected CX IDs
    const selectedCxIdValues = new Set(
      form.selectedCxIds.map((opt) => opt.value),
    );
    const selectedCxIdDetails =
      familyDetail.cxIds?.filter((cxIdDetail) =>
        selectedCxIdValues.has(cxIdDetail.cxId),
      ) ?? [];

    let cxIdIntersection: Set<string>;
    if (selectedCxIdDetails.length === 0) {
      cxIdIntersection = new Set<string>();
    } else {
      // Start with the first selected CxId's sourceLocales
      cxIdIntersection = new Set(selectedCxIdDetails[0].sourceLocales ?? []);
      // Intersect with each subsequent selected CxId
      for (let i = 1; i < selectedCxIdDetails.length; i++) {
        const locales = new Set(selectedCxIdDetails[i].sourceLocales ?? []);
        cxIdIntersection.forEach((locale) => {
          if (!locales.has(locale)) {
            cxIdIntersection.delete(locale);
          }
        });
      }
    }

    // Step 2: Union the intersection with family-level sourceLocales
    const resultSet = new Set<string>(cxIdIntersection);
    familyDetail.sourceLocales?.forEach((locale) => resultSet.add(locale));

    return Array.from(resultSet)
      .sort()
      .map((locale) => ({ label: locale, value: locale }));
  }, [describeFamilyData, form.selectedCxIds]);

  // ─── Family dropdown options (when no default) ──────────────────────────
  const familySelectOptions: SelectProps.Option[] = useMemo(() => {
    const families = listFamiliesData?.listCxFamilies?.cxFamilies;
    if (!families) return [];
    return families.map((f) => ({
      label: f.cxFamilyId,
      value: f.cxFamilyId,
    }));
  }, [listFamiliesData]);

  // ─── Handlers ───────────────────────────────────────────────────────────
  const handleFamilyChange = ({
    detail,
  }: {
    detail: SelectProps.ChangeDetail;
  }) => {
    setSelectedFamilyOption(detail.selectedOption);
    // The family field lives in this component, so its dirty state is tracked
    // explicitly rather than through one of the hook's setters.
    form.markFieldDirty('cxFamily');
    // Reset dependent fields when family changes
    form.setSelectedCxIds([]);
    form.setSourceLocale(null);
    // …and make them quiet again: the user did not clear them, this component
    // did, so their "required" errors must not fire until they are re-touched.
    form.clearFieldDirty('cxIds', 'sourceLocale');
  };

  const handleDismiss = () => {
    if (!form.submitting) {
      form.resetForm();
      setSelectedFamilyOption(null);
      onDismiss();
    }
  };

  const handleSubmitClick = () => {
    form.handleSubmit(
      onSubmit,
      () => {
        setSelectedFamilyOption(null);
        onDismiss();
      },
      effectiveCxFamilyId,
      checkNameTaken,
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      size="large"
      header={t('create-sg-modal-header')}
      closeAriaLabel={t('create-sg-modal-close-aria')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={handleDismiss}
              disabled={form.submitting}
            >
              {t('create-sg-cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitClick}
              loading={form.submitting}
            >
              {t('create-sg-submit')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="l">
          <NoPiiNotice />

          {form.submitError && (
            <Alert
              type="error"
              dismissible
              dismissAriaLabel={t('create-sg-dismiss-error-aria')}
              onDismiss={() => form.setSubmitError(null)}
            >
              {form.submitError}
            </Alert>
          )}

          <FormField
            label={
              <>
                {t('create-sg-name-label')} <RequiredAsterisk />
              </>
            }
            constraintText={t('create-sg-name-constraint', {
              min: SG_NAME_MIN_LENGTH,
              max: SG_NAME_MAX_LENGTH,
            })}
            errorText={
              getNameError(t, form.name, form.shouldShowValidation('name')) ??
              form.nameTakenError
            }
          >
            <Input
              ariaRequired={true}
              value={form.name}
              placeholder={t('sgd-rename-placeholder')}
              onChange={({ detail }) => form.setName(detail.value)}
              disabled={form.submitting}
            />
          </FormField>

          {cxFamily ? (
            <FormField
              label={
                <>
                  {t('create-sg-cx-family-label')} <RequiredAsterisk />
                </>
              }
              constraintText={t('create-sg-cx-family-inherited')}
            >
              {/* `disabled` (not `readOnly`) so the field is visibly greyed out,
                  making it obvious the value is inherited and cannot be changed. */}
              <Input value={cxFamily} disabled />
            </FormField>
          ) : (
            <FormField
              label={
                <>
                  {t('create-sg-cx-family-label')} <RequiredAsterisk />
                </>
              }
              constraintText={t('create-sg-cx-family-constraint')}
              errorText={getCxFamilyError(
                t,
                selectedFamilyOption?.value || '',
                form.shouldShowValidation('cxFamily'),
              )}
            >
              <Select
                ariaRequired={true}
                selectedOption={selectedFamilyOption}
                placeholder={t('create-sg-cx-family-placeholder')}
                options={familySelectOptions}
                onChange={handleFamilyChange}
                disabled={form.submitting}
                loadingText={t('create-sg-cx-family-loading')}
                statusType={listFamiliesLoading ? 'loading' : 'finished'}
                filteringType="auto"
                filteringPlaceholder={t('create-sg-cx-family-filter')}
              />
            </FormField>
          )}

          <FormField
            label={
              <>
                {t('create-sg-description-label')} <RequiredAsterisk />
              </>
            }
            constraintText={t('create-sg-description-constraint', {
              max: SG_DESCRIPTION_MAX_LENGTH,
            })}
            errorText={getDescriptionError(
              t,
              form.description,
              form.shouldShowValidation('description'),
            )}
            stretch
          >
            <Textarea
              value={form.description}
              placeholder={t('create-sg-description-placeholder')}
              rows={3}
              onChange={({ detail }) => form.setDescription(detail.value)}
              disabled={form.submitting}
            />
          </FormField>

          <FormField
            label={
              <>
                {t('create-sg-team-name-label')} <RequiredAsterisk />
              </>
            }
            constraintText={t('create-sg-team-name-constraint')}
            errorText={getTeamNameError(
              t,
              form.teamName,
              form.shouldShowValidation('teamName'),
            )}
          >
            <Input
              ariaRequired={true}
              value={form.teamName}
              placeholder={t('create-sg-team-name-placeholder')}
              onChange={({ detail }) => form.setTeamName(detail.value)}
              disabled={form.submitting}
            />
          </FormField>

          <FormField
            label={
              <>
                {t('create-sg-cx-ids-label')} <RequiredAsterisk />
              </>
            }
            constraintText={t('create-sg-cx-ids-constraint')}
            errorText={getCxIdsError(
              t,
              form.selectedCxIds.length,
              form.shouldShowValidation('cxIds'),
            )}
          >
            {describeFamilyLoading ? (
              <span
                role="status"
                aria-label={t('create-sg-cx-ids-loading-aria')}
              >
                <Spinner />
              </span>
            ) : (
              <Multiselect
                selectedOptions={form.selectedCxIds}
                placeholder={t('create-sg-cx-ids-placeholder')}
                filteringType="auto"
                filteringPlaceholder={t('create-sg-cx-ids-filter')}
                options={cxIdOptions}
                enableSelectAll
                i18nStrings={{
                  selectAllText: t('create-sg-cx-ids-select-all'),
                }}
                onChange={({ detail }) => {
                  form.setSelectedCxIds([...detail.selectedOptions]);
                  // Changing the CX IDs invalidates the source locale, so this
                  // component clears it — the user did not. Clear its dirty flag
                  // too (same as handleFamilyChange) so the "required" error does
                  // not fire until the user re-touches the field or submits.
                  form.setSourceLocale(null);
                  form.clearFieldDirty('sourceLocale');
                }}
                disabled={
                  form.submitting ||
                  describeFamilyLoading ||
                  !effectiveCxFamilyId
                }
              />
            )}
          </FormField>

          <FormField
            label={
              <>
                {t('create-sg-source-locale-label')} <RequiredAsterisk />
              </>
            }
            constraintText={
              form.selectedCxIds.length === 0
                ? t('create-sg-source-locale-constraint-no-cx')
                : t('create-sg-source-locale-constraint')
            }
            errorText={getSourceLocaleError(
              t,
              form.sourceLocale,
              form.shouldShowValidation('sourceLocale'),
            )}
          >
            {describeFamilyLoading ? (
              <span
                role="status"
                aria-label={t('create-sg-source-locale-loading-aria')}
              >
                <Spinner />
              </span>
            ) : (
              <Select
                ariaRequired={true}
                selectedOption={form.sourceLocale}
                placeholder={t('create-sg-source-locale-placeholder')}
                options={sourceLocaleOptions}
                onChange={({ detail }) =>
                  form.setSourceLocale(detail.selectedOption)
                }
                disabled={
                  form.submitting ||
                  describeFamilyLoading ||
                  !effectiveCxFamilyId ||
                  form.selectedCxIds.length === 0
                }
              />
            )}
          </FormField>
        </SpaceBetween>
      </Form>
    </Modal>
  );
};

export default CreateStringGroupModal;
